import React, { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import "../Assets/Css/Table.css";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Form } from "react-bootstrap";
import { axiosConfig } from "../axiosAPI";
import GlobalDataTable from "../components/GlobalDataTable";
import PermissionGate from "../components/PermissionGate";
import { PERMISSIONS } from "../utils/permissionConstants";

const initialForm = {
  driverUuid: [],
  type: [],
  amount: "",
  description: "",
};

function rowMatchesKeyword(row, keyword) {
  const k = keyword.toLowerCase();
  const hay = [
    row.dateCreated,
    row.driverFullName,
    row.typeLocalized,
    row.type,
    row.amount,
    row.description,
    row.uuid,
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");
  return hay.includes(k);
}

function Transactions() {
  const [balance, setBalance] = useState([]);
  const auth = useAuth();
  const [driver, setDriver] = useState([]);
  const transactionsTableRef = useRef(null);

  const fetchTransactionsPage = useCallback(
    async ({ pageNo, rowCount, keyword }) => {
      const headers = {
        Authorization: "Bearer " + auth.token,
        "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
      };
      const base = `${process.env.REACT_APP_BASE_URL}/transactions`;

      try {
        if (keyword) {
          const response = await axiosConfig.get(base, { headers });
          const all = Array.isArray(response.data.data.result) ? response.data.data.result : [];
          const filtered = all.filter((r) => rowMatchesKeyword(r, keyword));
          const start = (pageNo - 1) * rowCount;
          const slice = filtered.slice(start, start + rowCount);
          return { result: slice, totalCount: filtered.length };
        }

        const params = new URLSearchParams({
          pageNo: String(pageNo),
          rowCount: String(rowCount),
        });
        const response = await axiosConfig.get(`${base}?${params.toString()}`, { headers });
        return {
          result: response.data.data.result,
          totalCount: response.data.data.totalCount,
        };
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
        return { result: [], totalCount: 0 };
      }
    },
    [auth.token]
  );

  const getDriver = async () => {
    try {
      const response = await axiosConfig.get(`${process.env.REACT_APP_BASE_URL}/users/drivers`, {
        headers: {
          Authorization: "Bearer " + auth.token,
          "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
        },
      });
      setDriver(response.data.data.result);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  const getBalance = async (id) => {
    if (id) {
      try {
        const response = await axiosConfig.get(
          `${process.env.REACT_APP_BASE_URL}/transactions/balance/${id}`,
          {
            headers: {
              Authorization: "Bearer " + auth.token,
              "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
            },
          }
        );
        setBalance(response.data.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
        });
      }
    } else {
      setBalance(0);
    }
  };

  useEffect(() => {
    getDriver();
  }, [auth.token]);

  const [visible, setVisible] = useState(false);
  const openAddDialog = () => {
    setFormData(initialForm);
    setBalance(0);
    setVisible(true);
  };

  const headerElement = (
    <div className="inline-flex align-items-center justify-content-center gap-2">
      <span className="font-bold white-space-nowrap">Add Transaction</span>
    </div>
  );

  const transactionsHeaderExtras = (
    <PermissionGate permission={PERMISSIONS.TRANSACTIONS.CREATE}>
      <button type="button" className="table-header-add-btn" onClick={openAddDialog}>
        <i className="fa fa-plus"></i>
        <span>Add Transaction</span>
      </button>
    </PermissionGate>
  );
  const footerContent = (
    <div className="modal-button">
      <Button
        label="Cancel"
        className="cancel"
        onClick={() => {
          setVisible(false);
          setBalance(0);
        }}
        autoFocus
      />
      <Button
        label="Save"
        type="button"
        onClick={() => {
          handleSubmit();
        }}
        className="save"
        autoFocus
      />
    </div>
  );

  const [formData, setFormData] = useState(initialForm);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "driverUuid") {
      getBalance(e.target.value);
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      await axiosConfig.post(`${process.env.REACT_APP_BASE_URL}/transactions`, formData, {
        headers: {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
          "x-api-key": "fab6c94fc0044b53bf5e886a8fef1325",
        },
      });
      transactionsTableRef.current?.reload();
      setFormData(initialForm);
      setVisible(false);
      setBalance(0);
    } catch {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });
    }
  };

  return (
    <>
      <div className="main">
        <div className="sidebar">
          <Sidebar />
        </div>
        <div className="dev-table">
          <Navbar />
          <div className="table-card">
            <div className="table">
              <GlobalDataTable
                ref={transactionsTableRef}
                fetchPage={fetchTransactionsPage}
                defaultRowCount={10}
                rowCountOptions={[10, 20, 50, 100]}
                searchPlaceholder="Search driver, type, amount, description…"
                emptyMessage="No transactions found."
                headerTitle={
                  <span>
                    <i className="fa fa-exchange me-2"></i>
                    Transactions
                  </span>
                }
                headerExtras={transactionsHeaderExtras}
                dataKey="uuid"
                showGridlines
                tableStyle={{ minWidth: "700px" }}
              >
                <Column field="driverFullName" header="DRIVER NAME" style={{ width: "20%" }}></Column>
                <Column field="dateCreated" header="DATE CREATED" style={{ width: "20%" }}></Column>
                <Column field="typeLocalized" header="TYPE" style={{ width: "20%" }}></Column>
                <Column field="amount" header="AMOUNT" style={{ width: "20%" }}></Column>
                <Column field="description" header="DESCRIPTION" style={{ width: "20%" }}></Column>
              </GlobalDataTable>
              <Dialog
                visible={visible}
                modal
                header={headerElement}
                footer={footerContent}
                style={{ width: "50rem" }}
                onHide={() => {
                  if (!visible) return;
                  setVisible(false);
                  setBalance(0);
                }}
              >
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Driver</Form.Label>
                        <select onChange={handleChange} name="driverUuid" className="form-select">
                          <option value="">Select</option>
                          {driver.map((x, index) => (
                            <option value={x.uuid} key={index}>
                              {x.fullName}
                            </option>
                          ))}
                        </select>
                        <span>Balance: {balance?.balance}</span>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Type</Form.Label>
                        <select onChange={handleChange} name="type" className="form-select">
                          <option>Select</option>
                          <option value={"Pay"}>Pay</option>
                          <option value={"Collect"}>Collect</option>
                        </select>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Amount</Form.Label>
                        <Form.Control
                          type="number"
                          name="amount"
                          placeholder="amount"
                          value={formData.amount}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="form-group">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          type="text"
                          name="description"
                          placeholder="text"
                          value={formData.description}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </Form>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Transactions;
